import type Phaser from 'phaser'
import type { MapConfig } from '@interfaces/index'

export const BASE_CELL_W = 52
export const BASE_CELL_H = 26

export interface IsoOrigin {
  x: number
  y: number
}

/**
 * Calculate the screen-space offset (top-left anchor) to center an isometric
 * grid on the current scene viewport.
 *
 * The isometric diamond for a cols×rows grid has:
 *   total iso width  = (cols + rows) * cellW / 2
 *   total iso height = (cols + rows) * cellH / 2
 *
 * We place the origin so the grid is centered horizontally and sits in the
 * upper-center of the screen (top 15 % padding).
 */
export function calcIsoOffset(
  scene: Phaser.Scene,
  map: MapConfig,
  cellW: number,
  cellH: number
): { offsetX: number; offsetY: number } {
  const screenW = scene.scale.width
  const screenH = scene.scale.height

  const isoW = (map.cols + map.rows) * cellW / 2
  const isoH = (map.cols + map.rows) * cellH / 2

  return {
    offsetX: screenW / 2 - isoW / 2 + map.rows * (cellW / 2),
    offsetY: screenH / 2 - isoH / 2,
  }
}

// Convert grid coordinates to isometric screen coordinates (with origin offset)
export function cartToIsoScreen(
  gx: number,
  gy: number,
  cellW: number,
  cellH: number,
  origin: IsoOrigin
): { x: number; y: number } {
  return {
    x: origin.x + (gx - gy) * (cellW / 2),
    y: origin.y + (gx + gy) * (cellH / 2),
  }
}

// Calculate dynamic cell size based on screen dimensions and grid size.
// Scales so the full grid fits within 85 % of the smaller viewport dimension.
//
// Invariants enforced to prevent sub-pixel rendering artifacts at grid edges:
//   • cellH is rounded to the nearest EVEN integer → cellH/2 is always an integer.
//   • cellW = 2 × cellH → maintains exact 2:1 isometric ratio, cellW/2 = cellH.
//   • All diamond vertices (gx−gy)×cellH and (gx+gy)×(cellH/2) are whole-pixel values.
export function calcCellSize(
  screenW: number,
  screenH: number,
  cols: number,
  rows: number
): { cellW: number; cellH: number } {
  const isoWidth = (cols + rows) * BASE_CELL_W / 2
  const isoHeight = (cols + rows) * BASE_CELL_H / 2
  const scaleX = (screenW * 0.85) / isoWidth
  const scaleY = (screenH * 0.60) / isoHeight
  const scale = Math.min(scaleX, scaleY, 1.5)
  const cellH = Math.max(12, Math.round(BASE_CELL_H * scale / 2) * 2)
  return { cellW: cellH * 2, cellH }
}

// Calculate origin offset to center the grid on screen.
// Results are rounded to integer pixels so tile vertices never land on sub-pixels.
export function calcGridOrigin(
  screenW: number,
  screenH: number,
  cols: number,
  rows: number,
  cellW: number,
  _cellH: number
): IsoOrigin {
  const isoW = (cols + rows) * cellW / 2
  return {
    x: Math.round(screenW / 2 - isoW / 2 + rows * cellW / 2),
    y: Math.round(screenH * 0.15),
  }
}
